import { AxiosResponse } from 'axios';
import { Linking, Platform } from 'react-native';
import { call, put, takeLatest } from 'redux-saga/effects';

import { getPackageById, getPackages } from '../../services/api';
import API from '../../utils/API';
import { getUserFacingHttpErrorKey } from '../../utils/helpers/httpErrorKey';
import {
	closeWebPaymentWindow,
	getWebPaymentWindow,
	navigateWebPaymentWindow,
} from '../../utils/webPaymentWindow';
import {
	packageFailure,
	packageRequest,
	packagesFailure,
	packagesRequest,
	packagesSuccess,
	packageSuccess,
	paymentFailure,
	paymentRequest,
	paymentSuccess,
} from './packagesActions';
import { rememberDeposit } from './rememberDeposit';

function* _openUrl(url: string) {
	if (!url) throw new Error('Missing checkoutUrl');

	console.log('[openUrl] Platform:', Platform.OS, 'URL:', url);

	if (Platform.OS === 'web') {
		window.location.href = url;
		return;
	}

	// Check if window is available in webview-like contexts
	if (typeof window !== 'undefined' && window.open) {
		try {
			// Try opening in a new window/tab first (works in webview contexts)
			window.open(url, '_blank');
			console.log('[openUrl] Opened via window.open');
			return;
		} catch (e) {
			console.warn('[openUrl] window.open failed:', e);
		}
	}

	// Fallback to React Native Linking
	try {
		const can: boolean = yield call([Linking, Linking.canOpenURL], url);
		console.log('[openUrl] canOpenURL result:', can);

		if (!can) {
			// Try anyway, sometimes canOpenURL is unreliable
			console.warn('[openUrl] canOpenURL returned false, trying anyway');
		}

		yield call([Linking, Linking.openURL], url);
		console.log('[openUrl] Opened via Linking.openURL');
	} catch (error: any) {
		console.error('[openUrl] All methods failed:', error);
		throw new Error(`Cannot open payment url: ${error.message}`);
	}
}

function* fetchPackagesWorker(action: ReturnType<typeof packagesRequest>) {
	try {
		const { start, limit, filters } = action.payload;
		const res: AxiosResponse = yield call(getPackages, {
			start,
			limit,
			...filters,
		});

		const items = Array.isArray(res?.data?.items) ? res.data.items : [];
		const total = typeof res?.data?.totalCount === 'number' ? res.data.totalCount : items.length;

		console.log('[saga] packages:', { total, items });
		yield put(packagesSuccess({ items, total }));
	} catch (e: any) {
		console.log('[saga] packages error:', e);
		const msg = e?.message || e?.response?.data?.error || 'Failed to load packages';
		yield put(packagesFailure(msg));
	}
}

function* fetchPackageWorker(action: ReturnType<typeof packageRequest>) {
	try {
		const packageId = action.payload;
		const res: AxiosResponse = yield call(getPackageById, packageId);

		const packageData = res?.data;

		if (packageData) {
			yield put(packageSuccess(packageData));
		} else {
			yield put(packageFailure('Package not found'));
		}
	} catch (e: any) {
		console.log('[saga] package error:', e);
		const msg = e?.message || e?.response?.data?.error || 'Failed to load package';
		yield put(packageFailure(msg));
	}
}

function getPaymentEndpoint(provider: string): string {
	switch (provider) {
		case 'stripe':
		case 'link':
			return '/api/wallets/deposit/stripe';
		case 'paypal':
			return '/api/wallets/deposit/paypal'; // Future endpoint
		case 'epay':
		case 'easypay':
			return '/api/wallets/deposit/epay';
		default:
			return '/api/wallets/deposit/stripe';
	}
}

function* fetchPaymentWorker(action: ReturnType<typeof paymentRequest>) {
	try {
		console.log('[saga] payment data:', action.payload);
		const { amount, currency, pkg, frontendUrl, provider, recipient } = action.payload;

		const paymentData = {
			amount: Number(amount),
			currency: String(currency),
			frontendUrl,
			packageId: pkg.id,
			// EasyPay goes through the ePay deposit, which then returns a payment code instead of a
			// checkout page.
			...(provider === 'easypay' ? { paymentType: 'easypay' } : {}),
			// Link goes through the Stripe deposit, which then opens the Stripe checkout with Link.
			...(provider === 'link' ? { paymentType: 'link' } : {}),
			// A donation: the player pays, the recipient is credited (and the pair's chat unlocks).
			...(recipient ? { recipientPlayerId: recipient.playerId } : {}),
		};

		console.log('Payment data being sent:', paymentData);
		const endpoint = getPaymentEndpoint(provider);
		const response = yield call([API, API.bowaRequest], endpoint, 'post', paymentData, {
			'Content-Type': 'application/json',
			...API.getXCasinoHeader(),
		});

		console.log(`Full response after ${provider} payment:`, response);
		console.log('response.Data:', response.Data);
		console.log('response.Data.data:', response.Data.data);
		console.log(`response after ${provider} payment:`, response.Data.data);

		if (response.Data.error) {
			throw new Error(response.Data.error);
		}

		if (response.Data.status === 'success') {
			console.log('epay data', response.Data.data);
			console.log('Platform.OS:', Platform.OS); // 🆕 Log platform
			const { depositId, package: packageData } = response.Data.data;

			rememberDeposit({ depositId, packageData, pkg, recipient });

			if (provider === 'easypay') {
				const easyPayData = response.Data.data;
				if (!easyPayData?.IDN) {
					throw new Error('No EasyPay payment code received from payment server');
				}

				yield put(
					paymentSuccess({
						package: pkg,
						provider: 'easypay',
						transactionId: String(easyPayData.transaction_id ?? ''),
						paymentCode: String(easyPayData.IDN),
					}),
				);
				return;
			}

			if (provider.toLowerCase() === 'epay') {
				const epayData = response.Data.data;

				if (Platform.OS === 'web') {
					console.log('🌐 Web platform detected - submitting form');
					const epayPayload = {
						encoded: epayData.encoded,
						checksum: epayData.checksum,
						url_ok: epayData.url_ok,
						url_cancel: epayData.url_cancel,
						epayUrl: epayData.epayUrl,
					};
					const submittedInWindow = yield call(submitEPayFormInWindow, epayPayload);
					if (!submittedInWindow) {
						console.log('🌐 Payment window unavailable - submitting ePay form in current tab');
						yield call(submitEPayForm, epayPayload);
					}

					// Clear the payment loading state so the shop modal does not spin forever
					yield put(paymentSuccess({ provider: 'epay', isEpayForm: true }));
					return;
				}

				const epayFormHtml = createEPayFormHtml({
					encoded: epayData.encoded,
					checksum: epayData.checksum,
					url_ok: epayData.url_ok,
					url_cancel: epayData.url_cancel,
					epayUrl: epayData.epayUrl || 'https://demo.epay.bg/',
				});

				console.log('✅ ePay HTML form created, length:', epayFormHtml.length); // 🆕
				console.log('📦 Package data:', pkg); // 🆕
				console.log('🚀 Dispatching paymentSuccess for ePay'); // 🆕

				// Dispatch with the HTML content instead of URL
				yield put(
					paymentSuccess({
						checkoutUrl: epayFormHtml,
						package: pkg, // Use package from request
						provider: 'epay',
						isEpayForm: true,
					}),
				);
				console.log('✅ paymentSuccess dispatched successfully'); // 🆕
				return;
			}
			console.log('💳 Handling Stripe/PayPal');
			const checkoutUrl = response.Data.data.checkoutUrl;

			if (!checkoutUrl) {
				throw new Error('No checkout URL received from payment server');
			}

			console.log('Checkout URL received:', checkoutUrl);

			if (Platform.OS === 'web') {
				// For web, open the checkout in the payment tab so the game keeps running
				console.log('🌐 Opening checkout in payment window:', checkoutUrl);
				const openedInWindow = navigateWebPaymentWindow(checkoutUrl);
				if (!openedInWindow) {
					console.log('🌐 Payment window unavailable - redirecting current tab');
					window.location.href = checkoutUrl;
				}

				// Clear the payment loading state so the shop modal does not spin forever
				yield put(
					paymentSuccess({
						checkoutUrl,
						package: packageData,
						provider,
						isEpayForm: false,
					}),
				);
			} else {
				console.log('📱 Dispatching paymentSuccess for Stripe/PayPal');
				// For native Android, dispatch success with checkoutUrl
				// The UI component will open it in WebView
				yield put(
					paymentSuccess({
						checkoutUrl,
						package: packageData,
						provider,
						isEpayForm: false,
					}),
				);
			}

			return;
		}
		console.log("❌ Response status is not 'success':", response.Data);
		throw new Error('Unexpected response from payment server');
	} catch (err: any) {
		console.log('[saga] payment error:', err);
		console.log('[saga] payment error stack:', err.stack);

		closeWebPaymentWindow();

		yield put(
			paymentFailure(
				getUserFacingHttpErrorKey(err, {
					serverUnavailableKey: 'T_PAYMENT_SERVICE_UNAVAILABLE',
					authKey: 'T_SESSION_EXPIRED_RELOGIN',
					networkKey: 'T_NETWORK_ERROR_RETRY',
					clientErrorKey: 'T_PAYMENT_FAILED',
					fallbackKey: 'T_PAYMENT_FAILED',
				}),
			),
		);
	}
}

function createEPayFormHtml(data: {
	encoded: string;
	checksum: string;
	url_ok: string;
	url_cancel: string;
	epayUrl: string;
}): string {
	console.log('🔨 Creating ePay HTML form with data:', {
		epayUrl: data.epayUrl,
		hasEncoded: !!data.encoded,
		hasChecksum: !!data.checksum,
	});

	// IMPORTANT: Return proper HTML without extra escaping
	return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ePay Payment</title>
  <style>
    body {
      margin: 0;
      padding: 20px;
      font-family: Arial, sans-serif;
      background: #112345;
      color: white;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
    }
    .loader {
      text-align: center;
    }
    .spinner {
      border: 4px solid rgba(0, 240, 255, 0.3);
      border-radius: 50%;
      border-top: 4px solid #00f0ff;
      width: 40px;
      height: 40px;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  </style>
</head>
<body>
  <div class="loader">
    <div class="spinner"></div>
    <p>Redirecting to ePay payment...</p>
  </div>
  <form id="epayForm" method="POST" action="${data.epayUrl}" style="display:none;">
    <input type="hidden" name="PAGE" value="paylogin" />
    <input type="hidden" name="ENCODED" value="${data.encoded}" />
    <input type="hidden" name="CHECKSUM" value="${data.checksum}" />
    <input type="hidden" name="URL_OK" value="${data.url_ok}" />
    <input type="hidden" name="URL_CANCEL" value="${data.url_cancel}" />
  </form>
  <script>
    console.log('ePay form loaded, submitting...');
    setTimeout(function() {
      console.log('Auto-submitting form...');
      document.getElementById('epayForm').submit();
    }, 100);
  </script>
</body>
</html>`;
}

function submitEPayForm(data: {
	encoded: string;
	checksum: string;
	url_ok: string;
	url_cancel: string;
	epayUrl?: string;
}) {
	const form = document.createElement('form');
	form.method = 'POST';
	form.action = data.epayUrl || 'https://demo.epay.bg/';
	form.style.display = 'none';

	const fields = {
		PAGE: 'paylogin',
		ENCODED: data.encoded,
		CHECKSUM: data.checksum,
		URL_OK: data.url_ok,
		URL_CANCEL: data.url_cancel,
	};

	Object.entries(fields).forEach(([key, value]) => {
		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = key;
		input.value = value;
		form.appendChild(input);
	});

	document.body.appendChild(form);
	form.submit();
}

function submitEPayFormInWindow(data: {
	encoded: string;
	checksum: string;
	url_ok: string;
	url_cancel: string;
	epayUrl?: string;
}): boolean {
	const win = getWebPaymentWindow();
	if (!win) return false;

	try {
		const doc = win.document;
		// Post from the loading page itself, so it stays up until ePay answers instead of a blank tab.
		if (!doc.body) {
			doc.open();
			doc.write('<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body></body></html>');
			doc.close();
		}

		const form = doc.createElement('form');
		form.method = 'POST';
		form.action = data.epayUrl || 'https://demo.epay.bg/';
		form.style.display = 'none';

		const fields = {
			PAGE: 'paylogin',
			ENCODED: data.encoded,
			CHECKSUM: data.checksum,
			URL_OK: data.url_ok,
			URL_CANCEL: data.url_cancel,
		};

		Object.entries(fields).forEach(([key, value]) => {
			const input = doc.createElement('input');
			input.type = 'hidden';
			input.name = key;
			input.value = value;
			form.appendChild(input);
		});

		doc.body.appendChild(form);
		form.submit();
		return true;
	} catch (e) {
		console.warn('[saga] Failed to submit ePay form in payment window:', e);
		return false;
	}
}

export function* packageRootSaga() {
	yield takeLatest(packagesRequest.type, fetchPackagesWorker);
	yield takeLatest(packageRequest.type, fetchPackageWorker);
	yield takeLatest(paymentRequest.type, fetchPaymentWorker);
}
